import React, { useCallback, useEffect, useState } from 'react';
import { Button, DatePicker, Descriptions, Drawer, Grid, Input, Select, Space, Spin, Table, Tag, Timeline, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import tmService from '../../../services/tmService';
import { MESSAGE_STATUSES, MessageStatusTag, fmtTime, messageStatusText, toastErr } from './common';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const EVENT_COLOR = {
    accepted: 'blue', delivered: 'green', deferred: 'gold', bounced: 'red', complained: 'magenta', failed: 'red',
    rejected: 'gray', virus: 'red', spam_rejected: 'red', opened: 'cyan', clicked: 'cyan', unsubscribed: 'gray',
};

const MessageDrawer = ({ id, onClose }) => {
    const { t } = useTranslation();
    const screens = Grid.useBreakpoint();
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!id) return;
        setData(null);
        tmService.getMessage(id).then((r) => setData(r?.data || null)).catch((e) => toastErr(e, t('Failed to load')));
    }, [id, t]);

    const m = data?.message;
    return (
        <Drawer open={!!id} onClose={onClose} width={screens.md ? 640 : '100%'} title={t('Message details')} destroyOnClose>
            {!m ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div> : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="ID"><Text copyable style={{ fontSize: 12 }}>{m.id}</Text></Descriptions.Item>
                        <Descriptions.Item label={t('Status')}><MessageStatusTag status={m.status} /></Descriptions.Item>
                        <Descriptions.Item label={t('Recipient')}>{m.rcpt}</Descriptions.Item>
                        <Descriptions.Item label={t('From')}>{m.from_header || m.mail_from || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Subject')}>{m.subject || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Category')}>{m.category || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Source')}>{(m.source || '').toUpperCase()}</Descriptions.Item>
                        {m.smtp_response && <Descriptions.Item label={t('Response')}><Text style={{ fontSize: 12 }}>{m.smtp_code} {m.smtp_response}</Text></Descriptions.Item>}
                        {Array.isArray(m.tags) && m.tags.length > 0 && <Descriptions.Item label={t('Tag')}>{m.tags.map((x) => <Tag key={x}>{x}</Tag>)}</Descriptions.Item>}
                    </Descriptions>
                    <Timeline
                        items={(data.events || []).map((e) => ({
                            color: EVENT_COLOR[e.type] || 'gray',
                            children: (
                                <div>
                                    <Text strong>{e.type}</Text> <Text type="secondary" style={{ fontSize: 12 }}>{fmtTime(e.at)}</Text>
                                    {e.data && (e.data.smtp_response || e.data.mx || e.data.url || e.data.virus_name || e.data.bounce_type) && (
                                        <div style={{ fontSize: 12, wordBreak: 'break-all' }}>
                                            <Text type="secondary">
                                                {[e.data.smtp_code, e.data.smtp_response, e.data.bounce_type, e.data.mx, e.data.ip, e.data.url, e.data.virus_name].filter(Boolean).join(' · ')}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            ),
                        }))}
                    />
                </Space>
            )}
        </Drawer>
    );
};

const Messages = ({ stream }) => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ current: 1, pageSize: 50 });
    const [f, setF] = useState({});
    const [rcptInput, setRcptInput] = useState('');
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                stream_id: stream.id, page: page.current, per_page: page.pageSize,
                status: f.status, source: f.source, category: f.category || undefined, rcpt: f.rcpt || undefined,
                since: f.range?.[0]?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                until: f.range?.[1]?.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            };
            const r = await tmService.listMessages(params);
            setRows(r?.data?.items || []);
            setTotal(r?.data?.total || 0);
        } catch (e) {
            toastErr(e, t('Failed to load'));
        } finally {
            setLoading(false);
        }
    }, [stream.id, page, f, t]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (patch) => { setF((x) => ({ ...x, ...patch })); setPage((p) => ({ ...p, current: 1 })); };
    const categories = (stream.categories || []).map((c) => ({ value: c.code, label: c.name || c.code }));

    const columns = [
        { title: t('Time'), dataIndex: 'created_at', width: 160, render: fmtTime },
        { title: t('Recipient'), dataIndex: 'rcpt', ellipsis: true },
        { title: t('Subject'), dataIndex: 'subject', ellipsis: true, responsive: ['md'] },
        { title: t('Category'), dataIndex: 'category', width: 110, render: (v) => v || '-', responsive: ['lg'] },
        { title: t('Source'), dataIndex: 'source', width: 70, render: (v) => (v || '').toUpperCase(), responsive: ['lg'] },
        {
            title: t('Status'), dataIndex: 'status', width: 150,
            render: (v, m) => (
                <Space size={2} wrap>
                    <MessageStatusTag status={v} />
                    {m.opened_at && <Tag color="cyan" style={{ marginInlineEnd: 0 }}>{t('Opened')}</Tag>}
                    {m.content_flag === 'marketing' && <Tag color="purple" style={{ marginInlineEnd: 0 }}>{t('Marketing')}</Tag>}
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Recipient')} style={{ width: 200 }} value={rcptInput}
                    onChange={(e) => { setRcptInput(e.target.value); if (!e.target.value) setFilter({ rcpt: '' }); }}
                    onSearch={(v) => setFilter({ rcpt: v.trim() })} />
                <Select allowClear placeholder={t('Status')} style={{ width: 120 }} value={f.status}
                    onChange={(v) => setFilter({ status: v })}
                    options={MESSAGE_STATUSES.map((s) => ({ value: s, label: messageStatusText(s) }))} />
                <Select allowClear placeholder={t('Source')} style={{ width: 100 }} value={f.source}
                    onChange={(v) => setFilter({ source: v })} options={[{ value: 'api', label: 'API' }, { value: 'smtp', label: 'SMTP' }]} />
                {categories.length > 0 ? (
                    <Select allowClear placeholder={t('Category')} style={{ width: 140 }} value={f.category}
                        onChange={(v) => setFilter({ category: v })} options={categories} />
                ) : (
                    <Input allowClear placeholder={t('Category')} style={{ width: 120 }} onPressEnter={(e) => setFilter({ category: e.target.value.trim() })}
                        onChange={(e) => { if (!e.target.value) setFilter({ category: '' }); }} />
                )}
                <RangePicker value={f.range} onChange={(v) => setFilter({ range: v })} />
                <Button icon={<ReloadOutlined />} onClick={load} />
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={rows} columns={columns}
                onRow={(m) => ({ onClick: () => setOpenId(m.id), style: { cursor: 'pointer' } })}
                pagination={{ ...page, total, showSizeChanger: true, pageSizeOptions: [20, 50, 100, 200], onChange: (current, pageSize) => setPage({ current, pageSize }) }} />
            <Text type="secondary" style={{ fontSize: 12 }}>{t('Kept for 30 days')}</Text>
            <MessageDrawer id={openId} onClose={() => setOpenId(null)} />
        </div>
    );
};

export default Messages;
